<template>
  <div>
    <!-- Element Items Controls -->
    <ElementsListControls
      :disabled="
        elementsService.matches('loading') || elementsService.matches('create')
      "
      :element-group="elementsService.context.elementGroup"
      @elements-toggle="
        sendToElementsService('CHANGE_GROUP', { elementGroup: $event })
      "
      @close-click="$emit('close-click')"
      @upload-click="sendToElementsService('CREATE')"
    >
      <ElementsListNav
        v-if="showElementsListNav"
        slot="element-list-nav"
        :loading="elementsService.matches('loading')"
        :items="elementTypes"
        :selected-type="elementsService.context.elementTypeId"
        @item-click="
          sendToElementsService('CHANGE_TYPE', { elementTypeId: $event })
        "
      />
    </ElementsListControls>

    <!-- Errors -->
    <Error
      v-if="elementsService.matches('active')"
      :errors="elementsService.context.errors"
      class="mt-4"
    />

    <!-- No Elements Found -->
    <div
      v-if="elementsService.matches('active.empty')"
      class="mt-4 empty-element-item"
    >
      <p class="mb-0">Sorry we couldn't find any images.</p>
      <p
        v-if="elementsService.context.elementGroup === 'custom'"
        class="mt-2 mb-0"
      >
        <button
          class="text-brand font-lato"
          @click="sendToElementsService('CREATE')"
        >
          Upload
        </button>
        some now to get started!
      </p>
    </div>

    <ElementItemCreator
      v-if="elementsService.matches('create')"
      class="mt-4"
      :selected-type="elementsService.context.elementTypeId"
      @close-click="sendToElementsService('CLOSE_CREATE')"
      @element-added="sendToElementsService('ELEMENT_ADDED')"
    />

    <!-- <DEVELOPMENT_ElementItemCreator
      v-if="elementsService.matches('create') && DEVELOPMENT"
      class="mt-4"
      @close-click="sendToElementsService('CLOSE_CREATE')"
    /> -->

    <PromotionalBanner
      v-if="showPromotionalBanner"
      title="Premium Feature 🔒"
      subtitle="Customising templates with uploaded images is only available on paid plans..."
      button-label="Read More"
      button-label-slot-active="Read Less"
      img-path="heros/3d-elements.jpg"
      class="premium-banner my-6"
      button-class="premium-banner-button"
    >
      <template #hidden>
        <p>
          To help you in designing your newsletters you can upload and place
          images into your designs.
        </p>

        <p>
          When you start sending or downloading the newsletter you'll be asked
          to upgrade.
        </p>

        <p>
          Uploads from free accounts that don't upgrade will be removed from our
          servers over time.
        </p>
      </template>
    </PromotionalBanner>

    <Error
      v-if="featureCheckService.matches('error')"
      message="We couldn't check the status of this feature. Click retry to ensure you can use it."
      class="mt-4"
      :error-action="onRetryFeatureCheck"
      error-action-label="Retry?"
    />

    <!-- Element List Masonry -->
    <ElementsListMasonry
      v-if="
        elementsService.matches('active.withElements') ||
          elementsService.matches('loading.more')
      "
      :elements="elementsService.context.elements"
      :selected-element-id="selectedElementId"
      :upgrade-required="featureCheckService.matches('upgradeRequired')"
      @element-click="$emit('element-click', $event)"
    />

    <!-- Show when elements are loading -->
    <Loader
      v-if="elementsService.matches('loading')"
      label="Loading Images..."
    />

    <!-- v-show="!loadingMore" -->
    <!-- Setting the key prop below will reset the InfiniteLoader when the type changes -->
    <!-- <InfiniteLoader
      v-if="elementsService.matches('active.withElements')"
      :key="elementTypeId"
      :load="infiniteLoaderOnLoad"
      class="grey xs mx-auto mt-3 mb-6"
      :loading="elementsService.matches('loading')"
    >
      {{ elementsService.matches("loading") ? "Loading..." : "Load more..." }}
    </InfiniteLoader> -->
    <Button
      v-if="elementsService.matches('active.withElements.waiting')"
      class="secondary xs mx-auto"
      @click="onLoadMoreClick"
      >{{
        elementsService.matches("loading") ? "Loading..." : "Load More"
      }}</Button
    >
  </div>
</template>

<script>
import { computed } from "@vue/composition-api";
import { useMachine } from "@xstate/vue";
import elementsListMachine from "@/components/Elements/ElementsList/elements-list-machine";
import featureCheckMachine from "@/machines/feature-check-machine";
import ElementsListControls from "@/components/Elements/ElementsList/ElementsListControls";
import ElementsListNav from "@/components/Elements/ElementsList/ElementsListNav";
import ElementsListMasonry from "@/components/Elements/ElementsList/ElementsListMasonry";
import ElementItemCreator from "@/components/Elements/ElementItemCreator";
import PromotionalBanner from "@/components/UI/PromotionalBanner";
// FIXME: Create a dynamic import for the DEVELOPMENT_ElementItemCreator
// // import DEVELOPMENT_ElementItemCreator from "@/components/Elements/DEVELOPMENT_ElementItemCreator";
// import InfiniteLoader from "@/components/Utils/InfiniteLoader/InfiniteLoader";
import Loader from "@/components/UI/Loader";
import Error from "@/components/UI/Error";

import { watchState } from "@/utils/machine-helpers";

export default {
  name: "ElementsList",
  inject: ["DEVELOPMENT"],
  setup(props) {
    const { elementTypeId, elementTypes, selectedElementId } = props;
    // Initiate the machine...
    const { state: elementsService, send: sendToElementsService } = useMachine(
      elementsListMachine,
      {
        context: {
          elementTypeId,
          selectedElementId,
        },
      }
    );

    watchState(elementsService, "ElementItems");

    const {
      state: featureCheckService,
      send: sendToFeatureCheckService,
    } = useMachine(featureCheckMachine, {
      context: {
        feature: "customElements",
      },
    });

    const showPromotionalBanner = computed(
      () =>
        featureCheckService.value.matches("upgradeRequired") &&
        elementsService.value.context.elementGroup === "custom"
    );

    const showElementsListNav = computed(
      () =>
        elementTypes.length > 1 &&
        elementsService.value.matches("active.withElements") &&
        elementsService.value.context.elementGroup === "promo"
    );

    return {
      elementsService,
      featureCheckService,
      sendToElementsService,
      sendToFeatureCheckService,
      showElementsListNav,
      showPromotionalBanner,
    };
  },
  components: {
    ElementItemCreator,
    ElementsListControls,
    ElementsListMasonry,
    ElementsListNav,
    Error,
    // InfiniteLoader,
    Loader,
    PromotionalBanner,
  },
  props: {
    elementTypes: {
      default: () => [],
      type: Array,
    },
    elementTypeId: {
      default: "",
      type: String,
    },
    selectedElementId: {
      type: String,
      default: "",
    },
  },
  methods: {
    onLoadMoreClick() {
      this.sendToElementsService("LOAD_MORE_ELEMENTS");
    },
    onRetryFeatureCheck() {
      this.sendToFeatureCheckService("RETRY");
    },
  },
};
</script>

<style scoped>
>>> .error {
  @apply p-3 mb-4;

  .title {
    @apply text-sm mb-2;
  }

  .list {
    @apply mb-0;
  }

  .list-item {
    @apply text-sm;
  }
}

.empty-element-item {
  @apply mb-4 text-support border border-grey-lighter rounded-lg p-2 shadow bg-grey-lightest text-center text-sm;
}

.premium-banner {
  @apply mb-4;
}

>>> .premium-banner-button {
  @apply p-0 no-underline text-brand -mt-1;
}
</style>
